import { DesignSystemProvider } from '@quickbit/qb-design-system';
import { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { fetchAPI, ErrorResponse } from './api';
import { App } from './App';
import {
  Tab,
  Header,
  Loading,
  EmptyContent,
  Balances,
  TruncatedString,
} from './components';
import { SessionProvider, useSession } from './context/Session';
import {
  jsonToCsv,
  downloadCsv,
  formatErrorResponse,
  formatCurrency,
} from './utils';

interface Props {
  tabs: Tab[];
  merchantName: string | undefined;
}

export const BackofficeProviders = ({ children }: { children: ReactNode }) => {
  return (
    <BrowserRouter>
      <DesignSystemProvider>
        <SessionProvider>{children}</SessionProvider>
      </DesignSystemProvider>
    </BrowserRouter>
  );
};

export const BackofficeApp = ({ tabs, merchantName }: Props) => {
  return <App tabs={tabs} merchantName={merchantName} />;
};

/**
 * Component for rendering different tabs based on user permissions.
 *
 * @param {Props} props tabs
 */
export const Backoffice = ({ tabs, merchantName }: Props) => {
  return (
    <BackofficeProviders>
      <BackofficeApp tabs={tabs} merchantName={merchantName} />
    </BackofficeProviders>
  );
};
/**
 * NOTE: This is done so that the developers
 * can test changes by directly running the application
 */
if (process.env.REACT_APP_LOCAL_DEVELOPMENT === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('root')!);
  root.render(
    <BackofficeProviders>
      <BackofficeApp tabs={[]} merchantName="Local development" />
    </BackofficeProviders>
  );
}

//NOTE: First import then export all components and util fns that other apps need from this lib.
//  Make sure to export types as `type TypeName`.
export {
  Header,
  Loading,
  EmptyContent,
  Balances,
  TruncatedString,
  fetchAPI,
  type Tab,
  type ErrorResponse,
  jsonToCsv,
  downloadCsv,
  formatErrorResponse,
  formatCurrency,
  useSession,
};
